import { useEffect, useState } from 'react';

const CHECK_INTERVAL_MS = 5 * 60 * 1000;

interface VersionResponse {
    version: string;
}

async function fetchDeployedVersion(): Promise<string | null> {
    try {
        const response = await fetch('/version.json', { cache: 'no-store' });
        if (!response.ok) return null;
        const data: VersionResponse = await response.json();
        return data.version ?? null;
    } catch {
        return null;
    }
}

export function useVersionCheck(): boolean {
    // Skip entirely in dev — the dev server has no version.json and HMR handles updates.
    const isDev = import.meta.env.DEV;

    const [updateAvailable, setUpdateAvailable] = useState(false);

    useEffect(() => {
        if (isDev) return;

        async function check(): Promise<void> {
            const deployed = await fetchDeployedVersion();
            if (deployed !== null && deployed !== __APP_VERSION__) {
                setUpdateAvailable(true);
            }
        }

        check();

        const intervalId = setInterval(check, CHECK_INTERVAL_MS);

        function handleVisibilityChange(): void {
            if (document.visibilityState === 'visible') {
                check();
            }
        }

        document.addEventListener('visibilitychange', handleVisibilityChange);

        return () => {
            clearInterval(intervalId);
            document.removeEventListener('visibilitychange', handleVisibilityChange);
        };
        // isDev is stable for the lifetime of the app; including it satisfies the linter
        // without causing spurious re-registrations.
    }, [isDev]);

    return updateAvailable;
}
